import preferencesServices from '@/services/preferences.services.js';
import storage from '@/plugins/storage.plugins.js';
import mixin from '@/mixin/index.js';
import messages from "@/constants/messages.js";
import labels from "@/constants/labels.js";
import router from '@/router/index.js';
import categories from "@/constants/categories.js";
import contentRoutes from "@/constants/content.routes.js";
import { pickRecommendations } from "@/constants/home.recommendations.js";
const { _getTimestamp, _getLabel, _isCordovaApp, _getElapsedSeconds } = mixin.methods;

/** Chaves de persistência local da Home (adaptador de storage existente). */
const RECENT_STORAGE_KEY = 'home_recent_v1';
const FAVORITES_STORAGE_KEY = 'home_favorites_v1';

const RECENT_LIMIT = 20;
const RECOMMENDATIONS_COUNT = 5; // mobile mostra os 5, desktop os 3 primeiros
const FAVORITES_TTL = 600000; // 10 min
const FAVORITES_TIMEOUT = 8000; // 8s

/** Rotas cujos dados de conteúdo `extractFavoriteItemFromPath` sabe extrair. */
const TRACKABLE_ROUTE_NAMES = ['drugs', 'products', 'item', 'item-content', 'calculators'];

const withTimeout = (promise, ms) => {
    let timer;
    const timeout = new Promise((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const persistLocal = (key, items) => {
    storage.storageUpsert('configuration', key, { v: 1, items: items || [] }, () => { }, () => { });
};

const readLocal = async (key) => {
    try {
        const stored = await storage.storageSelect('configuration', key);
        return stored && Array.isArray(stored.items) ? stored.items : null;
    } catch {
        return null;
    }
};

async function getCardsCount(state) {
    let cards_count;
    try {
        cards_count = await storage.storageSelect('configuration', 'cards_count');
        let currentTime = new Date();
        let pastTime = new Date(cards_count.timestamp);
        let hoursPassed = (currentTime.getTime() - cards_count.timestamp) / (3600000);
        let isNewDay = currentTime.getDate() !== pastTime.getDate();
        cards_count.count = hoursPassed > 24 || isNewDay ? 0 : cards_count.count;
        state.answersDailyLimit = cards_count.count > 14;
    } catch (err) {
        state.answersDailyLimit = false;
        cards_count = {
            count: 0,
            timestamp: new Date().getTime()
        };
    }
    return cards_count;
}

const returnOnlyDrugsAndVaccines = (listOfItems) => {
    return listOfItems.filter(s => {
        return !!['1', '2', '4', '5'].includes(s.category)
    });
}
function globalSearchCreateClasses(list) {
    let classes = {
        'Doenças': { items: [] },
        'Medicamentos Sistêmicos': { items: [] },
        'Algoritmos': { items: [] }
    };
    for (let item of (list || [])) {
        const key = item.topic || categories[item.category - 1].portugueseName;
        if (!classes[key]) classes[key] = { items: [] }
        classes[key].items.push(item);
    }
    return classes;
}

const root = { root: true };

const modes = {
    'recents': {
        name: _getLabel('MY_RECENTS'),
        mode: 'recents',
        orderByClasses: true,
        hideBrandSelection: true,
        handler: async ({ state }) => {
            const now = _getTimestamp();
            if (!!state.recentlySeen && (now - state.recentlySeenIssued < 300000)) {
                state.preferences = state.recentlySeen;
                return;
            }
            state.recentlySeenIssued = now;
            state.recentlySeen = await preferencesServices.getRecentlySeen();
            state.preferences = state.recentlySeen;
        }
    },
    'searches': {
        name: process.env.VUE_APP_ENV === "partners" ? null : _getLabel('SEARCH_TEXT'),
        mode: 'searches',
        orderByClasses: true,
        hideBrandSelection: true,
        handler: async ({ state }) => {
            const now = _getTimestamp();
            if (!!state.searchList || !!state.searchListIssued) {
                state.preferences = globalSearchCreateClasses(state.searchList);
                return;
            }
            state.searchListIssued = now;
            state.searchList = await storage.storageSelect('global_summary');

            try {
                const concatListFromVersion = await preferencesServices.getSearchListMajorVersion();
                state.searchList = state.searchList.concat(concatListFromVersion);
            } finally {
                state.preferences = globalSearchCreateClasses(state.searchList);
                state.searchListIssued = null;
            }
        }
    },
    'drugs': {
        name: _getLabel('ITEMS_MODES_DRUGS'),
        mode: 'drugs',
        orderByClasses: false,
        hideBrandSelection: false,
        handler: async ({ state }) => {
            const now = _getTimestamp();
            if (!!state.searchList || !!state.searchListIssued) {
                if (state.searchList) {
                    state.preferences = returnOnlyDrugsAndVaccines(state.searchList);
                }
                return;
            }
            state.searchListIssued = now;
            state.searchList = await storage.storageSelect('global_summary');
            state.preferences = returnOnlyDrugsAndVaccines(state.searchList);
            const concatListFromVersion = await preferencesServices.getSearchListMajorVersion();
            state.searchList = state.searchList.concat(concatListFromVersion);
            state.preferences = returnOnlyDrugsAndVaccines(state.searchList);
            state.searchListIssued = null;
        }
    },
    'favorites': {
        name: _getLabel('MY_FAVORITES'),
        mode: 'favorites',
        orderByClasses: false,
        hideBrandSelection: true,
        handler: async ({ state, dispatch }) => {
            await dispatch('getFavorites');
            state.preferences = state.favorites;
        }
    }
}

function extractFavoriteItemFromPath(rootGetters) {
    let data;
    switch (router.app._route.name) {
        case 'drugs':
            data = rootGetters['drugs/drug'];
            return { name: data.name, id: data.id, category: data.category.id };
        case 'products':
            data = rootGetters['drugs/product'];
            return { name: data.name, id: data.id, category: data.category.id, type: 1 };
        case 'item':
        case 'item-content':
            data = rootGetters['topics/item'];
            return { name: data.name, id: data.id, topic: rootGetters['topics/topic'].name };
        default:
            data = rootGetters['calculators/calculator'];
            return { name: data.name, id: data.id, category: 7 };
    }
}

function calculateScoreAndProgressForContent(state, hasPreviousAnswer, card, previousScore) {
    if (state.cardsProgress && state.cardsProgress.length) {
        state.cardsProgress = state.cardsProgress.map(s => {
            if (s.content_id == card.content_id) {
                if (!hasPreviousAnswer) {
                    s.average_latest_score = ((s.average_latest_score * s.answers_total) + card.score) / (s.answers_total + 1);
                    s.answers_total += 1;
                    s.progress = Math.round((s.answers_total / s.total) * 100);
                } else {
                    s.average_latest_score = ((s.average_latest_score * s.answers_total) + card.score - previousScore) / s.answers_total;
                }
                s.average_latest_score = Math.round(s.average_latest_score * 10) / 10;
            }
            return s;
        });
    }
}

export default {
    async openRecentlySeen({ dispatch }) {
        dispatch("general/logEvent", 'openRecentlySeen', root);
        dispatch('general/routePathRedirect', { path: '/items/recents' }, root)
    },
    async openFavorites({ dispatch }) {
        dispatch("general/logEvent", 'openFavorites', root);
        dispatch('general/routePathRedirect', { path: '/items/favorites' }, root)
    },
    async openGlobalSearches({ dispatch }) {
        dispatch("general/logEvent", 'openGlobalSearches', root);
        dispatch('general/routePathRedirect', { path: '/items/searches' }, root);
    },
    async openDrugs({ dispatch }) {
        dispatch("general/logEvent", 'openDrugs', root);
        dispatch('general/routePathRedirect', { path: '/items/drugs' }, root)
    },
    async getSearchList({ state }) {
        state.searchList = await storage.storageSelect('global_summary');
    },
    async getPreferencesByMode({ commit, dispatch, state, rootGetters }, { mode, handleAction = true }) {
        try {
            const category = rootGetters['drugs/category'];
            const id = !!category && ['1', '2', '4', '5'].includes(category.id) ? category.id : null;
            commit('drugs/setCategory', {
                orderByClasses: modes[mode].orderByClasses,
                portugueseName: modes[mode].name,
                name: modes[mode].mode,
                hideBrandSelection: modes[mode].hideBrandSelection,
                vaccines_suplements_serums: false,
                human_products: false,
                id,
            }, root);
            if (handleAction) {
                await modes[mode].handler({ commit, dispatch, state, rootGetters });
            }
        } catch {
            state.recentlySeen = null;
            if (!_isCordovaApp()) {
                dispatch("general/showError", null, root);
                dispatch('general/routeRedirect', { name: 'home' }, root);
            }
        }
    },
    async postFakeDoorClick({ commit }, resource) {
        commit('general/setConfirmModel', {
            active: true,
            text: _getLabel(`FAKE_DOORS_${{
                'DISEASES_FULL_APPROACH': 'DISEASES_FULL_APPROACH',
            }[resource]}`),
            confirmText: _getLabel("FAKE_DOOR_YES"),
            closeText: _getLabel("FAKE_DOOR_NO"),
            action: 'preferences/postFakeDoorClickConfirm',
            parameters: { resource, confirm: true },
            cancelAction: 'preferences/postFakeDoorClickConfirm',
            cancelParameters: { resource, confirm: false },
        }, root);
        await preferencesServices.postFakeDoorClick({ resource });
    },
    async postFakeDoorClickConfirm({ commit }, { resource, confirm }) {
        console.log({ resource, confirm });
        await preferencesServices.postFakeDoorClick({ resource, confirm });
    },
    async openCardsMenu({ dispatch }) {
        dispatch("general/logEvent", 'openCardsMenu', root);
        dispatch('general/routePathRedirect', { path: '/cards' }, root);
    },
    async getCardsProgress({ commit, dispatch, state }) {
        try {
            if (!state.searchList || !state.searchList.length) {
                await dispatch('getSearchList');
            }
            dispatch("general/logEvent", 'getCardsProgress', root);
            commit("general/setLoadingOn", null, root);
            const progress = await preferencesServices.getCardsProgress();
            state.cardsProgress = progress;
        } catch (error) {
            state.cardsProgress = [];
            dispatch("general/showError", {
                back: true,
                event: 'getSearchList',
                error,
                color: 'error',
                message: messages.ERROR
            }, root);
        } finally {
            commit("general/setLoadingOff", null, root);
        }
    },
    async getCardsByContentId({ commit, state, dispatch }, content_id) {
        try {
            dispatch("general/logEvent", { name: 'getCardsByContentId', object: { 'id': content_id } }, root);
            commit("general/setLoadingOn", null, root);
            await getCardsCount(state);
            const cards = await preferencesServices.getCardsByContentId(content_id);
            if (!cards || !cards.length) {
                return dispatch('general/showAlert', { text: messages.FLASH_CARDS_NO_DATA_FOR_ITEM }, root);
            }
            state.cards = cards;
        } catch {
            state.cards = [];
            dispatch("general/showError", null, root);
        } finally {
            commit("general/setLoadingOff", null, root);
        }
    },
    async getCardsByProps({ commit, state, dispatch }, { content_type, card_class }) {
        try {
            dispatch("general/logEvent", { name: 'getCardsByProps', object: { 'id': content_type } }, root);
            commit("general/setLoadingOn", null, root);
            await getCardsCount(state);
            const cards = await preferencesServices.getCardsByProps(content_type, card_class);
            if (!cards || !cards.length) {
                return dispatch('general/showAlert', { text: messages.FLASH_CARDS_NO_DATA_FOR_ITEM }, root);
            }
            state.cards = cards;
        } catch {
            state.cards = [];
            dispatch("general/showError", null, root);
        } finally {
            commit("general/setLoadingOff", null, root);
        }
    },
    async postCardAnswers({ commit, state, dispatch, rootGetters }, { card, previousAnswer }) {
        try {
            const { hasPreviousAnswer, previousScore } = previousAnswer;
            dispatch("general/logEvent", { name: 'postCardAnswers', object: { 'id': card.card_id || '' } }, root);
            calculateScoreAndProgressForContent(state, hasPreviousAnswer, card, previousScore);
            let retries = 0;
            let cards_count = await getCardsCount(state);
            if (state.answersDailyLimit && !rootGetters['general/isPremium']) {
                return;
            }
            while (state.postingCardsAnswers) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                retries += 1;
                if (retries > 5) {
                    state.postingCardsAnswers = false;
                    break;
                }
            }
            if (!!card && !!state.cardsAnswersToPost) {
                state.cardsAnswersToPost.unshift(card);
            }
            state.postingCardsAnswers = true;
            if (!!state.cardsAnswersToPost && !!state.cardsAnswersToPost.length) {
                await preferencesServices.postCardAnswers(state.cardsAnswersToPost);
                cards_count = {
                    count: cards_count.count + state.cardsAnswersToPost.length,
                    timestamp: new Date().getTime()
                };
                storage.storageUpsert('configuration', 'cards_count', cards_count, () => { }, () => { });
                state.cardsAnswersToPost = [];
            }
        } catch (err) {
            console.log(err);
        } finally {
            state.postingCardsAnswers = false;
        }
    },
    /**
     * Busca os favoritos do usuário.
     * @param {object} [args]
     * @param {boolean} [args.silent] modo Home: sem loading global, sem toast de erro
     *   e respeitando o TTL de 10 min. Em falha mantém o snapshot local.
     * @param {boolean} [args.force] ignora o TTL (usado por "revalidar após alteração").
     */
    async getFavorites({ state, dispatch, commit }, args) {
        const { silent = false, force = false } = args || {};
        const now = _getTimestamp();
        const isFresh = !!state.favoritesIssued && (now - state.favoritesIssued) < FAVORITES_TTL;
        if (silent && !force && isFresh) {
            return state.favorites;
        }
        try {
            dispatch("general/logEvent", { name: 'getFavorites' }, root);
            if (!silent) commit("general/setLoadingOn", null, root);
            const response = await withTimeout(preferencesServices.getFavorites(), FAVORITES_TIMEOUT);
            const favorites = (response && response.favorites) || [];
            state.favorites = favorites;
            state.favoritesIssued = now;
            persistLocal(FAVORITES_STORAGE_KEY, favorites);
            return favorites;
        } catch {
            if (silent) {
                // A Home nunca quebra por causa de favoritos: mantém o snapshot já renderizado.
                dispatch("general/logEvent", { name: 'home_favorites_unavailable' }, root);
                return state.favorites;
            }
            state.favorites = [];
            dispatch("general/showError", null, root);
            return [];
        } finally {
            if (!silent) commit("general/setLoadingOff", null, root);
        }
    },

    /**
     * Registra o último conteúdo aberto neste dispositivo.
     * Ponto único de escrita de `deviceRecentlySeen`; chamado pelas views de
     * conteúdo somente após o carregamento bem-sucedido. Nunca lança.
     */
    async trackContentOpen({ state, commit, rootGetters }, item) {
        try {
            if (!item) {
                const routeName = router.app && router.app._route && router.app._route.name;
                if (!TRACKABLE_ROUTE_NAMES.includes(routeName)) return;
                item = extractFavoriteItemFromPath(rootGetters);
            }
            if (!item || !item.id || !item.name) return;
            const path = contentRoutes.resolveContentPath(item);
            if (!path) return;
            const entry = {
                id: item.id,
                name: item.name,
                category: item.category === undefined ? null : item.category,
                topic: item.topic || null,
                type: item.type || null,
                kind: contentRoutes.getContentKind(item),
                path,
                ts: _getTimestamp(),
            };
            const key = contentRoutes.getContentKey(entry);
            const recent = [entry]
                .concat((state.deviceRecentlySeen || []).filter(
                    (i) => contentRoutes.getContentKey(i) !== key
                ))
                .slice(0, RECENT_LIMIT);
            commit('setDeviceRecentlySeen', recent);
            persistLocal(RECENT_STORAGE_KEY, recent);
        } catch (err) {
            // Histórico local é acessório: falhas não podem interromper a navegação.
            console.warn('trackContentOpen failed', err);
        }
    },

    /** Carrega histórico local e snapshot de favoritos para a Home renderizar sem rede. */
    async hydrateHomeLocalData({ state, commit }) {
        const recent = await readLocal(RECENT_STORAGE_KEY);
        if (recent) {
            commit('setDeviceRecentlySeen', recent.slice(0, RECENT_LIMIT));
        }
        if (!state.favorites || !state.favorites.length) {
            const favorites = await readLocal(FAVORITES_STORAGE_KEY);
            if (favorites && favorites.length) {
                commit('setFavorites', favorites);
            }
        }
    },

    /** Limpa dados pessoais locais da Home. Chamado no logout. */
    clearHomeLocalData({ commit }) {
        commit('setDeviceRecentlySeen', []);
        commit('setFavorites', []);
        commit('setFavoritesIssued', null);
        commit('setHomeRecommendations', null);
        persistLocal(RECENT_STORAGE_KEY, []);
        persistLocal(FAVORITES_STORAGE_KEY, []);
    },

    /**
     * Sorteio local de recomendados a partir de `global_summary`. Zero rede.
     * Sorteia de novo a cada abertura da Home; "Ver outros" evita repetir o
     * sorteio imediatamente anterior.
     */
    async getHomeRecommendations({ state, commit, dispatch, rootGetters }) {
        try {
            let pool = state.searchList;
            if (!pool || !pool.length) {
                pool = await storage.storageSelect('global_summary');
                state.searchList = pool;
            }
            if (!pool || !pool.length) {
                throw new Error('global_summary unavailable');
            }

            const excludedKeys = new Set();
            for (const favorite of (state.favorites || [])) {
                const key = contentRoutes.getContentKey(favorite);
                if (key) excludedKeys.add(key);
            }
            const lastSeenKey = contentRoutes.getContentKey((state.deviceRecentlySeen || [])[0]);
            if (lastSeenKey) excludedKeys.add(lastSeenKey);

            const previousKeys = new Set(
                (state.homeRecommendations || [])
                    .map((i) => contentRoutes.getContentKey(i))
                    .filter(Boolean)
            );

            const picked = pickRecommendations({
                pool,
                count: RECOMMENDATIONS_COUNT,
                isPremium: !!rootGetters['general/isPremium'],
                excludedKeys,
                previousKeys,
            });
            commit('setHomeRecommendations', picked);
            return picked;
        } catch {
            dispatch("general/logEvent", { name: 'home_recommended_unavailable' }, root);
            commit('setHomeRecommendations', []);
            return [];
        }
    },

    /** Abre um conteúdo (favorito, recente ou recomendado) pela rota resolvida. */
    openContent({ dispatch }, item) {
        const opened = contentRoutes.openContentItem({ item, dispatch });
        if (!opened) {
            dispatch("general/showError", null, root);
        }
        return opened;
    },
    async postFavorites({ state, dispatch, commit, rootGetters }, args) {
        let { favorite, confirmExclusion } = args || {};
        try {
            dispatch("general/logEvent", { name: 'postFavorites' }, root);
            if (!state.favorites.length) {
                await dispatch('getFavorites');
            }
            if (!favorite) {
                favorite = extractFavoriteItemFromPath(rootGetters);
            }
            const exclusionMode = state.favorites.find(f => f.id === favorite.id);
            if (exclusionMode && !confirmExclusion) {
                return commit('general/setConfirmModel', {
                    text: router.app._route.name === 'items-mode' ? messages.FAVORITE_CONFIRM_EXCLUSION_FROM_LIST : messages.FAVORITE_CONFIRM_EXCLUSION,
                    callback: () => {
                        dispatch('postFavorites', { favorite, confirmExclusion: true })
                    },
                    confirmText: labels.YES,
                    closeText: labels.NO,
                }, root);
            } else if (!exclusionMode && state.favorites.length >= 100) {
                return commit('general/setConfirmModel', {
                    text: messages.FAVORITE_ADD_LIMIT,
                    confirmText: labels.CONTINUE,
                    alert: true,
                }, root);
            }
            favorite['date'] = new Date().getTime();
            commit("general/setLoadingOn", null, root);
            const { favorites, lastOperation } = await preferencesServices.postFavorites(favorite);
            dispatch('general/showAlert', { text: lastOperation === 'delete' ? messages.FAVORITE_DELETE : messages.FAVORITE_ADD }, root);
            state.favorites = favorites;
            state.preferences = favorites;
            state.favoritesIssued = _getTimestamp();
            persistLocal(FAVORITES_STORAGE_KEY, favorites);
            state.triggerFavoritesChange = new Date().getTime();
        } catch (err) {
            dispatch("general/showError", null, root);
        } finally {
            commit("general/setLoadingOff", null, root);
        }
    },
    async getAndUpdateRecentSearches({ state, dispatch, commit, rootGetters }, item) {
        let recentSearches;
        try {
            recentSearches = await storage.storageSelect('configuration', 'recent_searches');
            if (!recentSearches) {
                throw new Error('No search items');
            }
        } catch (err) {
            recentSearches = [];
        }
        if (item && item.id) {
            recentSearches = recentSearches.filter(i => i.id !== item.id);
            item = state.searchList.find(s => s.id === item.id);
            if (item) {
                recentSearches.unshift(item);
            }
            if (recentSearches.length > 6) {
                recentSearches = recentSearches.slice(0, 6);
            }
            storage.storageUpsert('configuration', 'recent_searches', recentSearches);
        }
        return recentSearches;
    },
    async getAds({ dispatch, state, commit }, { location, index, breakpoint }) {
        try {
            dispatch("general/logEvent", { name: 'getAds' }, root);
            return await preferencesServices.getAds(location, breakpoint, index);
        } catch (err) {
            return [];
        }
    },
    async postAdsClick({ state, dispatch, commit, rootGetters }, { confirm, ads_uid, requestTime }) {
        dispatch("general/logEvent",
            {
                name: confirm ? "adsConfirm" : "adsClose",
                object: {
                    user: rootGetters['general/user'].uid,
                    ads: ads_uid
                },
            },
            root);
        await preferencesServices.postAdsClick(
            confirm,
            _getElapsedSeconds(requestTime),
            ads_uid
        );
    },
}

